import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase'
import { useCurrentAdvisor } from './useCurrentAdvisor'

export function useDMConversations() {
  const { advisor } = useCurrentAdvisor()
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async (advisorId) => {
    setLoading(true)
    // Fetch conversations and join other advisor info
    const { data: convs } = await supabase
      .from('sfp_dm_conversations')
      .select(`
        id, last_message_at, last_message_preview,
        advisor_a_id, advisor_b_id,
        advisor_a:advisors!sfp_dm_conversations_advisor_a_id_fkey(id, full_name, name, avatar_url),
        advisor_b:advisors!sfp_dm_conversations_advisor_b_id_fkey(id, full_name, name, avatar_url)
      `)
      .or(`advisor_a_id.eq.${advisorId},advisor_b_id.eq.${advisorId}`)
      .order('last_message_at', { ascending: false, nullsFirst: false })

    if (!convs) { setLoading(false); return }

    // Compute unread count per conversation
    const convIds = convs.map(c => c.id)
    let unreadMap = {}
    if (convIds.length > 0) {
      // Get all messages in my conversations that are NOT from me and NOT deleted
      const { data: msgs } = await supabase
        .from('sfp_dm_messages')
        .select('id, conversation_id, sender_id')
        .in('conversation_id', convIds)
        .neq('sender_id', advisorId)
        .is('deleted_at', null)

      const msgIds = (msgs ?? []).map(m => m.id)
      let readIds = new Set()
      if (msgIds.length > 0) {
        const { data: reads } = await supabase
          .from('sfp_dm_reads')
          .select('message_id')
          .in('message_id', msgIds)
          .eq('advisor_id', advisorId)
        readIds = new Set((reads ?? []).map(r => r.message_id))
      }

      for (const msg of msgs ?? []) {
        if (!readIds.has(msg.id)) {
          unreadMap[msg.conversation_id] = (unreadMap[msg.conversation_id] ?? 0) + 1
        }
      }
    }

    const enriched = convs.map(c => ({
      ...c,
      otherAdvisor: c.advisor_a_id === advisorId ? c.advisor_b : c.advisor_a,
      unreadCount: unreadMap[c.id] ?? 0,
    }))

    setConversations(enriched)
    setLoading(false)
  }, [])

  useEffect(() => {
    if (!advisor?.id) return
    fetch(advisor.id)

    const ch = supabase
      .channel(`dm-convs-${advisor.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sfp_dm_conversations' }, () => fetch(advisor.id))
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sfp_dm_messages' }, () => fetch(advisor.id))
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sfp_dm_reads' }, () => fetch(advisor.id))
      .subscribe()

    return () => supabase.removeChannel(ch)
  }, [advisor?.id, fetch])

  const getOrCreateConversation = useCallback(async (otherAdvisorId) => {
    if (!advisor?.id) return null
    const [a, b] = [advisor.id, otherAdvisorId].sort()

    // Try to find existing
    const { data: existing } = await supabase
      .from('sfp_dm_conversations')
      .select('id')
      .eq('advisor_a_id', a)
      .eq('advisor_b_id', b)
      .maybeSingle()

    if (existing) return existing.id

    const { data: created } = await supabase
      .from('sfp_dm_conversations')
      .insert({ advisor_a_id: a, advisor_b_id: b })
      .select('id')
      .single()

    return created?.id ?? null
  }, [advisor?.id])

  return { conversations, loading, getOrCreateConversation, refresh: () => advisor?.id && fetch(advisor.id) }
}
